import type { ReactNode } from 'react';

import { Button, type ButtonProps } from 'components/ui/button';
import { cn } from 'lib/utils';

export interface ActionPromptCardAction {
  label: string;
  onClick: () => void;
  /** Project Button variant; omit for the primary style. */
  variant?: ButtonProps['variant'];
  /** Extra classes on the button (e.g. `hidden sm:inline-flex`). */
  className?: string;
}

export interface ActionPromptCardProps {
  message: ReactNode;
  actions: ActionPromptCardAction[];
  disabled?: boolean;
  className?: string;
}

/** Message with action buttons; stacks on mobile, row on sm+ (Figma 8768:129353). */
export function ActionPromptCard({
  message,
  actions,
  disabled,
  className,
}: Readonly<ActionPromptCardProps>) {
  return (
    <div
      className={cn(
        'flex w-full flex-col items-stretch gap-4 rounded-[12px] border border-grey-100 bg-white p-4 shadow-[0_10px_20px_rgba(173,174,163,0.2)] dark:border-border dark:bg-card sm:flex-row sm:items-center',
        className
      )}
    >
      <div className="min-w-0 flex-1 text-sm font-medium leading-[1.4] text-grey-900 dark:text-foreground">
        {message}
      </div>
      <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:shrink-0">
        {actions.map((action) => (
          <Button
            key={action.label}
            type="button"
            size="sm"
            variant={action.variant}
            disabled={disabled}
            className={cn('flex-1 sm:flex-initial', action.className)}
            onClick={action.onClick}
          >
            {action.label}
          </Button>
        ))}
      </div>
    </div>
  );
}
